import React from 'react';
import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';

/**
 * Google 登入按鈕 Props
 */
interface GoogleLoginButtonProps extends Omit<ButtonProps, 'onClick'> {
  onClick: () => Promise<void>;
  isLoading?: boolean;
  text?: string;
}

/**
 * Google 登入按鈕元件
 *
 * 提供美觀的 Google OAuth 登入按鈕，符合 Google 品牌規範
 */
export const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onClick,
  isLoading = false,
  text = '使用 Google 登入',
  disabled,
  ...props
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleClick = async () => {
    setIsProcessing(true);
    try {
      await onClick();
    } finally {
      setIsProcessing(false);
    }
  };

  const loading = isLoading || isProcessing;

  return (
    <Button
      fullWidth
      variant="outlined"
      size="large"
      onClick={handleClick}
      disabled={disabled || loading}
      startIcon={
        loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <GoogleIcon />
        )
      }
      sx={{
        borderColor: '#dadce0',
        color: '#3c4043',
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '14px',
        py: 1.5,
        '&:hover': {
          borderColor: '#d2e3fc',
          backgroundColor: '#f8f9fa',
        },
        '&:active': {
          backgroundColor: '#e8f0fe',
        },
        '&.Mui-disabled': {
          borderColor: '#dadce0',
          color: '#80868b',
        },
        ...props.sx,
      }}
      {...props}
    >
      {loading ? '處理中...' : text}
    </Button>
  );
};

export default GoogleLoginButton;
